import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../config/api';
import './OfferUploadRecipientPage.css';

const ROLE_OPTIONS = [
  { value: 'BUYER_AGENT', label: 'Buyer Agent' },
  { value: 'BUYER', label: 'Buyer' },
  { value: 'OTHER', label: 'Other' },
];

const OfferUploadRecipientPage = () => {
  const { linkId, addressSlug } = useParams();
  // Optional cosmetic address segment (/offer/{addressSlug}/{linkId}) — kept when navigating.
  const offerBasePath = addressSlug ? `/offer/${addressSlug}/${linkId}` : `/offer/${linkId}`;
  const navigate = useNavigate();

  const [metadata, setMetadata] = useState(null);
  const [metadataError, setMetadataError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Returning submitter shortcut: once OTP has been verified, the session JWT
  // is cached in localStorage keyed by linkId. Next time they open the link,
  // skip the identify + OTP steps entirely and jump to the upload page. The
  // upload page re-validates the token via /me — if the agent disabled the
  // link or the token is otherwise invalid, the user gets bounced back here
  // with a "Verify again" button.
  useEffect(() => {
    try {
      const cached = localStorage.getItem(`offerLinkSession:${linkId}`);
      if (cached) {
        navigate(`${offerBasePath}/upload?session=${encodeURIComponent(cached)}`, { replace: true });
      }
    } catch { /* storage unavailable — fall through to normal flow */ }
    // Intentionally runs once per linkId mount. Any later verification writes
    // through this effect's cleanup path (handleVerifyOtp sets the key then
    // navigates).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkId]);

  const [step, setStep] = useState('form'); // 'form' | 'otp'
  const [role, setRole] = useState('BUYER_AGENT');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [otp, setOtp] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [otpError, setOtpError] = useState(null);
  const [otpSentAt, setOtpSentAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get(API_ENDPOINTS.publicOfferLinkMetadata(linkId));
        setMetadata(res.data);
      } catch (e) {
        setMetadataError(e?.response?.data?.message || 'This offer upload link is no longer available.');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [linkId]);

  const buyerNameRequired = role === 'BUYER';

  const buildSubmitterPayload = () => ({
    role,
    email: email.trim().toLowerCase(),
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    phone: phone.trim(),
    buyerName: buyerName.trim() || null,
  });

  const handleSubmitInfo = async (evt) => {
    evt.preventDefault();
    setFormError(null);
    if (!email || !firstName || !lastName || !phone) {
      setFormError('Please fill in every required field.');
      return;
    }
    if (buyerNameRequired && !buyerName.trim()) {
      setFormError('Buyer name is required.');
      return;
    }
    setIsSubmitting(true);
    try {
      await apiClient.post(
        API_ENDPOINTS.publicOfferLinkSubmitterInfo(linkId),
        buildSubmitterPayload(),
      );
      setOtpSentAt(Date.now());
      setStep('otp');
    } catch (e) {
      setFormError(e?.response?.data?.message || 'Could not send code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (evt) => {
    evt.preventDefault();
    setOtpError(null);
    if (!/^\d{6}$/.test(otp)) {
      setOtpError('Enter the 6-digit code from the email.');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await apiClient.post(
        API_ENDPOINTS.publicOfferLinkVerifyOtp(linkId),
        { email: email.trim().toLowerCase(), code: otp },
      );
      const sessionToken = res?.data?.sessionToken;
      if (!sessionToken) {
        throw new Error('No session token in response');
      }
      // Persist the session so a returning submitter skips OTP next time.
      // JWT has no `exp` — the agent still revokes access by disabling the
      // parent offer-upload link. localStorage (not session-) so the shortcut
      // survives tab close.
      try {
        localStorage.setItem(`offerLinkSession:${linkId}`, sessionToken);
      } catch { /* storage unavailable — fall back to URL-only session */ }
      navigate(`${offerBasePath}/upload?session=${encodeURIComponent(sessionToken)}`);
    } catch (e) {
      setOtpError(e?.response?.data?.message || 'That code did not match. Please check your email again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setOtpError(null);
    setIsSubmitting(true);
    try {
      await apiClient.post(
        API_ENDPOINTS.publicOfferLinkSubmitterInfo(linkId),
        buildSubmitterPayload(),
      );
      setOtpSentAt(Date.now());
    } catch (e) {
      setOtpError(e?.response?.data?.message || 'Could not resend code.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="offer-public-shell">
        <div className="offer-public-card">
          <div className="offer-public-header">
            <h1>Submit an Offer</h1>
            <p>Preparing your secure upload link</p>
          </div>
          <div className="offer-public-body offer-public-loading">
            <div className="offer-public-spinner" aria-hidden="true" />
            <p className="offer-public-loading-text">Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  if (metadataError || !metadata) {
    return (
      <div className="offer-public-shell">
        <div className="offer-public-card">
          <div className="offer-public-header">
            <h1>Submit an Offer</h1>
          </div>
          <div className="offer-public-body">
            <p className="offer-public-error">{metadataError || 'This offer upload link is no longer available.'}</p>
            <p className="offer-public-hint">If you received this link recently, ask the listing agent to share a fresh one.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!metadata.enabled) {
    return (
      <div className="offer-public-shell">
        <div className="offer-public-card">
          <div className="offer-public-header">
            <h1>Submit an Offer</h1>
          </div>
          <div className="offer-public-body">
            <p className="offer-public-error">This offer upload link is no longer available.</p>
            <p className="offer-public-hint">Please contact {metadata.agentName || 'the listing agent'} for updated instructions.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="offer-public-shell">
      <div className="offer-public-card">
        <div className="offer-public-header">
          <h1>Submit an Offer</h1>
          <p>
            {metadata.agentName
              ? `${metadata.agentName} is accepting offers for`
              : 'Offers are being accepted for'}
            <br />
            <strong>{metadata.propertyAddress}</strong>
          </p>
        </div>

        {step === 'form' && (
          <form className="offer-public-body" onSubmit={handleSubmitInfo} noValidate>
            <p className="offer-public-lead">
              Enter your details to receive a one-time verification code by email and text message. After verifying, you'll be able to upload your offer package.
            </p>
            <div className="offer-public-field">
              <label htmlFor="role">I am a</label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="offer-public-select"
              >
                {ROLE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="offer-public-field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="offer-public-field-row">
              <div className="offer-public-field">
                <label htmlFor="firstName">First name</label>
                <input id="firstName" type="text" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
              </div>
              <div className="offer-public-field">
                <label htmlFor="lastName">Last name</label>
                <input id="lastName" type="text" autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
              </div>
            </div>
            <div className="offer-public-field">
              <label htmlFor="phone">Phone</label>
              <input id="phone" type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required />
            </div>
            <div className="offer-public-field">
              <label htmlFor="buyerName">
                Buyer name {buyerNameRequired ? '' : <span className="offer-public-optional">(optional)</span>}
              </label>
              <input
                id="buyerName"
                type="text"
                value={buyerName}
                onChange={(e) => setBuyerName(e.target.value)}
                required={buyerNameRequired}
                placeholder={buyerNameRequired ? '' : "Buyer's full name, if known"}
              />
            </div>
            {formError && <p className="offer-public-error">{formError}</p>}
            <button className="offer-public-primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending code…' : 'Continue'}
            </button>
            <p className="offer-public-fineprint">
              We'll email a 6-digit code to verify it's you before you upload any documents.
            </p>
          </form>
        )}

        {step === 'otp' && (
          <form className="offer-public-body" onSubmit={handleVerifyOtp} noValidate>
            <p className="offer-public-lead">
              We emailed a 6-digit code to <strong>{email}</strong> and texted it to the phone number you entered. Enter it below to continue to the upload page.
            </p>
            <div className="offer-public-field">
              <label htmlFor="otp">Verification code</label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                className="offer-public-otp-input"
                required
              />
            </div>
            {otpError && <p className="offer-public-error">{otpError}</p>}
            <button className="offer-public-primary" type="submit" disabled={isSubmitting || otp.length !== 6}>
              {isSubmitting ? 'Verifying…' : 'Verify & Continue'}
            </button>
            <div className="offer-public-resend-row">
              <button type="button" className="offer-public-link-btn" onClick={handleResend} disabled={isSubmitting}>
                Resend code
              </button>
              {otpSentAt && (
                <span className="offer-public-fineprint">
                  Last sent {new Date(otpSentAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
            </div>
            <button
              type="button"
              className="offer-public-link-btn offer-public-back"
              onClick={() => { setStep('form'); setOtp(''); setOtpError(null); }}
              disabled={isSubmitting}
            >
              Change details
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default OfferUploadRecipientPage;
